import { useQuery, QueryKey, QueryFunction, useQueryClient } from '@tanstack/react-query';
import { safeLocalStorage, safeSessionStorage, StorageType } from '@ifixit/utils/safeStorage';
import { SentryError } from '@ifixit/sentry';
import { z } from 'zod';

function getFromCache<T>(
   key: string,
   cacheTime: number,
   storageType: StorageType,
   schema: z.ZodType<T>,
   forceMiss?: boolean
): T | null {
   const storage = storageType === 'localStorage' ? safeLocalStorage : safeSessionStorage;
   const cached = storage.getJson<{ data: T; timestamp: number }>(key);

   if (!cached) {
      return null;
   }

   if (cacheDisabled() || forceMiss) {
      return null;
   }

   const isExpired = Date.now() - cached.timestamp > cacheTime;

   if (isExpired) {
      storage.removeItem(key);
      return null;
   }

   const { data, success } = schema.safeParse(cached.data);

   if (!success) {
      storage.removeItem(key);
      return null;
   }

   return data;
}

function setInCache<T>(key: string, data: T, storageType: StorageType) {
   const storage = storageType === 'localStorage' ? safeLocalStorage : safeSessionStorage;
   storage.setJson(key, { data, timestamp: Date.now() });
}

function cacheDisabled(): boolean {
   if (typeof window === 'undefined') return true; // No caching on server-side
   return new URL(window.location.href).searchParams.has('disableCacheGets');
}

/**
 * Recursively and deterministically stringify a value with sorted object keys.
 * Arrays preserve order, objects are sorted by key.
 */
function stableStringify(value: unknown): string {
   if (Array.isArray(value)) {
      return `[${value.map(stableStringify).join(',')}]`;
   }
   if (value !== null && typeof value === 'object') {
      return (
         '{' +
         Object.entries(value as Record<string, unknown>)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`)
            .join(',') +
         '}'
      );
   }
   return JSON.stringify(value);
}

function getCacheKey(queryKey: QueryKey): string {
   if (typeof queryKey === 'string') return queryKey;
   // Use stable stringify for arrays/objects
   try {
      return stableStringify(queryKey);
   } catch {
      return String(queryKey);
   }
}

type CacheableQueryOptions<T> = {
   queryKey: QueryKey;
   queryFn: QueryFunction<T>;
   cacheTime: number; // ms, if omitted disables persistent cache
   storage: StorageType; // required if persistentCacheTime is set
   schema: z.ZodType<T>;
   forceMiss?: boolean;
} & Omit<Parameters<typeof useQuery<T>>[0], 'queryKey' | 'queryFn'>;

export function useCacheableQuery<T>(options: CacheableQueryOptions<T>) {
   const { queryKey, queryFn, cacheTime, storage, forceMiss, staleTime, schema, ...queryOptions } =
      options;

   const queryClient = useQueryClient();

   if (!queryFn || typeof queryFn !== 'function') {
      throw new SentryError('queryFn must be provided and must be a function');
   }

   const cacheKey = getCacheKey(queryKey);

   const wrappedQueryFn: QueryFunction<T> = async context => {
      const cached = getFromCache<T>(cacheKey, cacheTime, storage, schema, forceMiss);

      if (cached) {
         return cached;
      }

      const data = await queryFn(context);
      setInCache(cacheKey, data, storage);
      return data;
   };

   const queryResult = useQuery<T>({
      ...queryOptions,
      queryKey,
      queryFn: wrappedQueryFn,
      staleTime: staleTime ?? cacheTime,
   });

   return {
      ...queryResult,
      clear: async () => {
         await queryClient.invalidateQueries({ queryKey });
         clear(queryKey, storage);
      },
   };
}

function clear(queryKey: QueryKey, storageType: StorageType) {
   const cacheKey = getCacheKey(queryKey);
   const storage = storageType === 'localStorage' ? safeLocalStorage : safeSessionStorage;
   storage.removeItem(cacheKey);
}
